/** @deprecated Just use the language */
export const isUndefined = <T>(x: T | undefined): x is undefined =>
  x === undefined;
/** @deprecated Just use the language */
export const isNotUndefined = <T>(x: T | undefined): x is T => !isUndefined(x);

/** @deprecated Just use the language */
export const isNull = <T>(x: T | null): x is null => x === null;
/** @deprecated Just use the language */
export const isNotNull = <T>(x: T | null): x is T => !isNull(x);

export type Nil = undefined | null;
export const isNotNil = <T>(x: T | Nil): x is T =>
  isNotUndefined(x) && isNotNull(x);
export const isNil = <T>(x: T | Nil): x is Nil => !isNotNil(x);

export const voidOp = (): void => void 0;

/** Expect that the given value is a `never`. This will always throw a runtime error.
 *   This is commonly used as the default case handler in an exhasutive switch statement.
 */
export function assertNever(x: never): never {
  throw new Error(`Unexpected object: ${x}`);
}

/** Expect that the given value is a `never`, but do not throw.
 * This is a compile-time only check and should be paired with some type of runtime action */
export const assertNeverWeak = (x: never): void => void 0;

/**
 * Makes optional members of an object type non-optional
 * https://github.com/microsoft/TypeScript/issues/28374#issuecomment-536521051
 */
export type DeepNonNullable<T> = {
  [P in keyof T]-?: NonNullable<T[P]>;
};

/** Makes fields of an object T given in Ks nonnull */
export type WithNonNullKeys<T, Ks extends keyof T> = {
  [K in keyof T]-?: K extends Ks ? NonNullable<T[K]> : T[K];
};

/**
 * Make optional members of an object required
 */
export type Require<
  Props extends object,
  Keys extends keyof Props = keyof Props
> = Prettify<Omit<Props, Keys> & Required<Pick<Props, Keys>>>;

/** Makes required members of an object optional */
export type WithPartial<
  Props extends Object,
  Keys extends keyof Props = keyof Props
> = Prettify<Omit<Props, Keys> & Partial<Pick<Props, Keys>>>;

/**
 * Like Partial, but deep
 * @see https://stackoverflow.com/a/61132308/11882366
 */
export type DeepPartial<T> = {
  [P in keyof T]?: DeepPartial<T[P]>;
};

/**
 * Makes specified members of an object required and the rest optional.
 * Useful for keeping the type intact while making a few fields required.
 * @see https://stackoverflow.com/questions/69327990
 */
export type PartialRequire<T, K extends keyof T> = Partial<T> &
  Required<Pick<T, K>>;

/**
 * A silly function that returns the input passed to the second function as the given type
 * This is useful in ad-hoc function compositions with Ramda, since its type-defs are far from perfect
 * It has the weird thunkify-d signature because you can't use generics on a point-free function
 */
export const setType =
  <T>() =>
  (x: unknown) =>
    x as T;

/**
 * Make complex intersection types easier to read in intellisense
 * @see https://twitter.com/mattpocockuk/status/1622730173446557697
 */
export type Prettify<T> = {
  [K in keyof T]: T[K];
} & {};

/**
 * Turns a value into any
 * @deprecated only used to make legacy modules fit TS without no-checking the whole file */
export const anyifyValue = <T>(x: T): any => x;

/**
 * Turns any function into a function that accepts and returns any
 * @deprecated only used to make legacy modules fit TS without no-checking the whole file */
export const anyifyFn = <T extends (...args: any[]) => any>(x: T): any => x;

/** Branded type implementation borrowed from egghead.io
 * @see https://egghead.io/blog/using-branded-types-in-typescript
 */
declare const __brand: unique symbol;
type Brand<B> = {[__brand]: B};
/** Create a branded type  */
export type Branded<T, B> = T & Brand<B>;

/** Unpacks an array type */
export type Unpacked<T> = T extends (infer U)[] ? U : T;

/** Used for array types that can not be empty */
export type NonEmptyArray<T> = [T, ...T[]];
